import { useCallback, useEffect, useState } from "react";
import hrTypeResolver from "../services/hrTypeResolver";
import { tylkoTypyOsobiste } from "../utils/hrTypZarzadczy";

/**
 * WNIOSKI, KTÓRE PRACOWNIK MOŻE ZŁOŻYĆ W SWOIM IMIENIU — jedno źródło dla kreatora z /wnioski-hr
 * (`HrNewRequestDrawer`) i dla karty „Wnioski pracownicze” w oknie „Utwórz nowe zgłoszenie”.
 *
 * [polecenie właściciela 2026-09-28] Karta w oknie zgłoszenia ma pokazywać „to co można złożyć w sekcji
 * /wnioski-hr”. Dlatego pobranie i zawężenie listy WYDZIELONO z kreatora do tego haka, zamiast skopiować:
 * dwa miejsca liczące listę osobno rozjechałyby się przy pierwszej zmianie reguły (typy zarządcze, typy
 * systemowe), a pracownik widziałby w oknie wniosek, którego w /wnioski-hr nie ma — albo odwrotnie.
 *
 * Reguła (brief 2026-08-30, pkt 6): serwer (`GET /hr-requests/available-types`) przysyła WYŁĄCZNIE typy
 * dostępne dla tego pracownika, pogrupowane w kategorie. Tu zostają z nich tylko typy OSOBISTE — zarządcze
 * mają własne wejście („Wniosek w imieniu pracownika”, patrz utils/hrTypZarzadczy).
 *
 * @param {boolean} aktywny  pobiera przy przejściu na `true` (otwarcie kreatora / wejście w kartę).
 * @returns {{ categories: Array|null, allTypes: Array, typesError: boolean, odswiez: Function }}
 *   `categories === null` — jeszcze nie wiadomo (ładowanie); `typesError` — awaria API, a NIE „nic nie przysługuje”.
 */
export const useDostepneTypyWnioskow = (aktywny) => {
    const [categories, setCategories] = useState(null);  // null = jeszcze nie wiadomo
    const [allTypes, setAllTypes] = useState([]);        // płaska lista DOSTĘPNYCH typów (wyszukiwarka)
    const [typesError, setTypesError] = useState(false); // awaria API != „nic nie przysługuje"
    const [proba, setProba] = useState(0);

    useEffect(() => {
        if (!aktywny) return undefined;
        let cancelled = false;
        (async () => {
            const przyslane = await hrTypeResolver.getAvailableTypesForUser();
            if (cancelled) return;
            if (przyslane === null) { setTypesError(true); setCategories([]); setAllTypes([]); return; }
            // Serwer odpowiada na pytanie „co WOLNO mi złożyć" — przełożonemu wolno też typy zarządcze.
            // Ale ten kreator nie ma kroku „Pracownik", więc zostawia sobie wyłącznie typy osobiste;
            // zarządcze mają własne wejście („Wniosek w imieniu pracownika"). Patrz utils/hrTypZarzadczy.
            const grupy = tylkoTypyOsobiste(przyslane);
            const flat = [];
            grupy.forEach((cat) => (cat.types || []).forEach((t) =>
                flat.push({ ...t, _catLabel: cat.label, _catBg: cat.bg, _catColor: cat.color })));
            setTypesError(false);
            setCategories(grupy.map((cat) => ({ ...cat, _typesCount: (cat.types || []).length })));
            setAllTypes(flat);
        })();
        return () => { cancelled = true; };
    }, [aktywny, proba]);

    /** „Spróbuj ponownie” po awarii — to samo zapytanie, bez przeładowania okna. */
    const odswiez = useCallback(() => {
        setCategories(null);
        setTypesError(false);
        setProba((p) => p + 1);
    }, []);

    return { categories, allTypes, typesError, odswiez };
};

export default useDostepneTypyWnioskow;
